import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { accommodationTypes } from '../constants/accommodationTypes';
import { tenantsOptions } from '../constants/tenants';
import { priceDurations } from '../constants/pricing';
import { amenitiesList } from '../constants/amenities';
import { policyOptions } from '../constants/policies';
import './RoomsExpandedFilters.css';

export const initialRoomsExpandedFilters = {
  accommodationTypes: [],
  tenants: [],
  priceRange: { min: '', max: '' },
  priceDuration: [],
  amenities: [],
  smokingPolicy: [],
  visitorsPolicy: [],
  petsPolicy: [],
};

// Slider bounds (EGP) — inputs can still go outside if needed later
const PRICE_SLIDER_MIN = 0;
const PRICE_SLIDER_MAX = 20000;
const PRICE_SLIDER_STEP = 100;

function RoomsExpandedFilters({
  isOpen,
  onClose,
  currentLanguage,
  getLocalizedText,
  filters,
  onApply,
}) {
  const [localFilters, setLocalFilters] = useState({
    ...initialRoomsExpandedFilters,
    ...filters,
    priceRange: { ...initialRoomsExpandedFilters.priceRange, ...(filters?.priceRange || {}) },
  });

  useEffect(() => {
    if (isOpen) {
      setLocalFilters({
        ...initialRoomsExpandedFilters,
        ...filters,
        priceRange: { ...initialRoomsExpandedFilters.priceRange, ...(filters?.priceRange || {}) },
      });
    }
  }, [isOpen, filters]);

  const sliderMin = useMemo(() => {
    const v = parseFloat(localFilters.priceRange?.min);
    return Number.isFinite(v) ? Math.min(Math.max(v, PRICE_SLIDER_MIN), PRICE_SLIDER_MAX) : PRICE_SLIDER_MIN;
  }, [localFilters.priceRange?.min]);

  const sliderMax = useMemo(() => {
    const v = parseFloat(localFilters.priceRange?.max);
    return Number.isFinite(v) ? Math.min(Math.max(v, PRICE_SLIDER_MIN), PRICE_SLIDER_MAX) : PRICE_SLIDER_MAX;
  }, [localFilters.priceRange?.max]);

  if (!isOpen) return null;

  // Multi-select (amenities only)
  const toggleArrayFilter = (name, value) => {
    setLocalFilters((prev) => {
      const arr = prev[name] || [];
      const isSelected = arr.includes(value);
      return {
        ...prev,
        [name]: isSelected ? arr.filter((v) => v !== value) : [...arr, value],
      };
    });
  };

  // Single-select (radio): accommodation, gender, duration, policies
  const setRadioFilter = (name, value) => {
    setLocalFilters((prev) => {
      const arr = prev[name] || [];
      const isSelected = arr.includes(value);
      return {
        ...prev,
        [name]: isSelected ? [] : [value],
      };
    });
  };

  const setPriceField = (field, value) => {
    setLocalFilters((prev) => {
      let min = field === 'min' ? value : prev.priceRange?.min ?? '';
      let max = field === 'max' ? value : prev.priceRange?.max ?? '';

      const minNum = parseFloat(min);
      const maxNum = parseFloat(max);
      // Keep min <= max when both are numbers
      if (Number.isFinite(minNum) && Number.isFinite(maxNum) && minNum > maxNum) {
        if (field === 'min') max = String(minNum);
        else min = String(maxNum);
      }

      return {
        ...prev,
        priceRange: { min: min === '' || min == null ? '' : String(min), max: max === '' || max == null ? '' : String(max) },
      };
    });
  };

  const handleSliderMin = (e) => {
    const val = Number(e.target.value);
    const capped = Math.min(val, sliderMax);
    setPriceField('min', capped);
  };

  const handleSliderMax = (e) => {
    const val = Number(e.target.value);
    const capped = Math.max(val, sliderMin);
    setPriceField('max', capped);
  };


  // Each selected value counts as 1 (min + max = 2, 10 amenities = 10, …)
  const countActiveFilters = (f) => {
    if (!f) return 0;
    let n = 0;
    n += (f.accommodationTypes || []).length;
    n += (f.tenants || []).length;
    if (f.priceRange?.min !== '' && f.priceRange?.min != null) n += 1;
    if (f.priceRange?.max !== '' && f.priceRange?.max != null) n += 1;
    n += (f.priceDuration || []).length;
    n += (f.amenities || []).length;
    n += (f.smokingPolicy || []).length;
    n += (f.visitorsPolicy || []).length;
    n += (f.petsPolicy || []).length;
    return n;
  };

  const activeFilterCount = countActiveFilters(localFilters);

  const handleApply = () => {
    onApply(localFilters);
    onClose();
  };

  const handleReset = () => {
    setLocalFilters(initialRoomsExpandedFilters);
    onApply(initialRoomsExpandedFilters);
  };

  // Track fill between thumbs
  const rangePercentLeft = ((sliderMin - PRICE_SLIDER_MIN) / (PRICE_SLIDER_MAX - PRICE_SLIDER_MIN)) * 100;
  const rangePercentRight = ((sliderMax - PRICE_SLIDER_MIN) / (PRICE_SLIDER_MAX - PRICE_SLIDER_MIN)) * 100;

  return createPortal(
    <div className="ref-overlay" onClick={onClose} dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}>
      <aside className="ref-panel" onClick={(e) => e.stopPropagation()}>
        <div className="ref-header">
          <h2>{getLocalizedText('الفلاتر', 'Filters')}</h2>
          <button type="button" className="ref-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="ref-content">
          {/* Accommodation type — radio segmented */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('نوع الإقامة', 'Accommodation type')}</h3>
            <div className="ref-segmented" role="radiogroup" aria-label={getLocalizedText('نوع الإقامة', 'Accommodation type')}>
              {accommodationTypes.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="radio"
                  aria-checked={(localFilters.accommodationTypes || []).includes(option.value)}
                  className={`ref-segment${(localFilters.accommodationTypes || []).includes(option.value) ? ' is-selected' : ''}`}
                  onClick={() => setRadioFilter('accommodationTypes', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Gender / tenants — radio segmented */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('النوع', 'Gender')}</h3>
            <div className="ref-segmented" role="radiogroup" aria-label={getLocalizedText('النوع', 'Gender')}>
              {tenantsOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="radio"
                  aria-checked={(localFilters.tenants || []).includes(option.value)}
                  className={`ref-segment${(localFilters.tenants || []).includes(option.value) ? ' is-selected' : ''}`}
                  onClick={() => setRadioFilter('tenants', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Price range + dual slider */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('نطاق السعر', 'Price range')}</h3>
            <p className="ref-section-sub">
              {getLocalizedText('بالجنيه المصري', 'In Egyptian pounds')}
            </p>

            <div className="ref-slider-wrap">
              <div className="ref-slider-track">
                <div
                  className="ref-slider-range"
                  style={{
                    left: `${rangePercentLeft}%`,
                    width: `${rangePercentRight - rangePercentLeft}%`,
                  }}
                />
              </div>
              <input
                type="range"
                className="ref-slider ref-slider-min"
                min={PRICE_SLIDER_MIN}
                max={PRICE_SLIDER_MAX}
                step={PRICE_SLIDER_STEP}
                value={sliderMin}
                onChange={handleSliderMin}
                aria-label={getLocalizedText('الحد الأدنى للسعر', 'Minimum price')}
              />
              <input
                type="range"
                className="ref-slider ref-slider-max"
                min={PRICE_SLIDER_MIN}
                max={PRICE_SLIDER_MAX}
                step={PRICE_SLIDER_STEP}
                value={sliderMax}
                onChange={handleSliderMax}
                aria-label={getLocalizedText('الحد الأقصى للسعر', 'Maximum price')}
              />
            </div>

            <div className="ref-price-inputs">
              <label className="ref-price-field">
                <span>{getLocalizedText('الحد الأدنى', 'Minimum')}</span>
                <div className="ref-price-input-wrap">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={localFilters.priceRange?.min ?? ''}
                    placeholder={String(PRICE_SLIDER_MIN)}
                    onChange={(e) => setPriceField('min', e.target.value)}
                  />
                </div>
              </label>
              <span className="ref-price-dash">—</span>
              <label className="ref-price-field">
                <span>{getLocalizedText('الحد الأقصى', 'Maximum')}</span>
                <div className="ref-price-input-wrap">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={0}
                    value={localFilters.priceRange?.max ?? ''}
                    placeholder={`${PRICE_SLIDER_MAX}+`}
                    onChange={(e) => setPriceField('max', e.target.value)}
                  />
                </div>
              </label>
            </div>

            {/* Duration — radio segmented (daily / weekly / …) */}
            <div
              className="ref-segmented ref-segmented-duration"
              style={{ marginTop: 14 }}
              role="radiogroup"
              aria-label={getLocalizedText('مدة السعر', 'Price duration')}
            >
              {priceDurations.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="radio"
                  aria-checked={(localFilters.priceDuration || []).includes(option.value)}
                  className={`ref-segment${(localFilters.priceDuration || []).includes(option.value) ? ' is-selected' : ''}`}
                  onClick={() => setRadioFilter('priceDuration', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Amenities — multi-select chips */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('المرافق', 'Amenities')}</h3>
            <div className="ref-chips">
              {amenitiesList.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={`ref-chip${(localFilters.amenities || []).includes(option.value) ? ' is-selected' : ''}`}
                  onClick={() => toggleArrayFilter('amenities', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Smoking — radio segmented */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('سياسة التدخين', 'Smoking policy')}</h3>
            <div className="ref-segmented" role="radiogroup" aria-label={getLocalizedText('سياسة التدخين', 'Smoking policy')}>
              {policyOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  role="radio"
                  aria-checked={(localFilters.smokingPolicy || []).includes(option.value)}
                  className={`ref-segment${(localFilters.smokingPolicy || []).includes(option.value) ? ' is-selected' : ''}`}
                  onClick={() => setRadioFilter('smokingPolicy', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Visitors — radio segmented */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('سياسة الزوار', 'Visitors policy')}</h3>
            <div className="ref-segmented" role="radiogroup" aria-label={getLocalizedText('سياسة الزوار', 'Visitors policy')}>
              {policyOptions.map((option) => (
                <button
                  type="button"
                  key={`visitors-${option.value}`}
                  role="radio"
                  aria-checked={(localFilters.visitorsPolicy || []).includes(option.value)}
                  className={`ref-segment${(localFilters.visitorsPolicy || []).includes(option.value) ? ' is-selected' : ''}`}
                  onClick={() => setRadioFilter('visitorsPolicy', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>

          {/* Pets — radio segmented */}
          <section className="ref-section">
            <h3 className="ref-section-title">{getLocalizedText('سياسة الحيوانات الأليفة', 'Pets policy')}</h3>
            <div className="ref-segmented" role="radiogroup" aria-label={getLocalizedText('سياسة الحيوانات الأليفة', 'Pets policy')}>
              {policyOptions.map((option) => (
                <button
                  type="button"
                  key={`pets-${option.value}`}
                  role="radio"
                  aria-checked={(localFilters.petsPolicy || []).includes(option.value)}
                  className={`ref-segment${(localFilters.petsPolicy || []).includes(option.value) ? ' is-selected' : ''}`}
                  onClick={() => setRadioFilter('petsPolicy', option.value)}
                >
                  {getLocalizedText(option.ar, option.en)}
                </button>
              ))}
            </div>
          </section>
        </div>

        <div className="ref-footer">
          <button type="button" className="ref-btn-clear" onClick={handleReset}>
            {getLocalizedText('مسح الكل', 'Clear all')}
          </button>
          <button type="button" className="ref-btn-apply" onClick={handleApply}>
            {activeFilterCount > 0
              ? getLocalizedText(`عرض النتائج (${activeFilterCount})`, `Show results (${activeFilterCount})`)
              : getLocalizedText('عرض النتائج', 'Show results')}
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
}

export default RoomsExpandedFilters;